/* eslint-disable no-underscore-dangle */
import { useEffect, useState } from 'react';
import PartnersService from '../Services/PartnersService';
import OperatorService from '../Services/OperatorService';

// Module-level caches so /payments table rows don't refetch the full
// partners + operators lists on every render. Pattern mirrors
// useAccountsCache.js — first caller starts the fetch, others await.
// No TTL: partners/operators change rarely; reload() invalidates.

let partnersPromise = null;
let operatorsPromise = null;

function getPartnersCached() {
  if (!partnersPromise) {
    partnersPromise = PartnersService.fetchPartners()
      .then((res) => res?.data?.data?.documents || res?.data?.data || [])
      .catch((err) => {
        partnersPromise = null;
        throw err;
      });
  }
  return partnersPromise;
}

function getOperatorsCached() {
  if (!operatorsPromise) {
    operatorsPromise = OperatorService.fetchOperators()
      .then((res) => res?.data?.data?.documents || res?.data?.data || [])
      .catch((err) => {
        operatorsPromise = null;
        throw err;
      });
  }
  return operatorsPromise;
}

export function invalidateCounterpartyCaches() {
  partnersPromise = null;
  operatorsPromise = null;
}

/**
 * Returns lookup maps for resolving partner_id / operator_id → display
 * name. Booking lookups are out of scope (too many ids to prefetch);
 * callers render booking-counterparty cells with the raw id as a tooltip.
 *
 * Returns: { partnersById: Map, operatorsById: Map, loading, error }
 *   partnersById.get(id) → partner doc with .name (or undefined)
 *   operatorsById.get(id) → operator doc with .name (or undefined)
 *
 * Both kinds spell the display name LOWERCASE `name` (OperatorSchema.ts and
 * PartnerSchema.ts agree) and both hash on CAPITAL `Id`. This comment used
 * to claim operators carried `.Name`, which no operator record does; it is
 * `NotificationGroup` that spells a name with a capital. Callers read
 * through `counterpartyLabel`, which tolerates either — but the shape to
 * write in a fixture is lowercase.
 */
export function useCounterpartyNames() {
  const [partners, setPartners] = useState([]);
  const [operators, setOperators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([getPartnersCached(), getOperatorsCached()])
      .then(([p, o]) => {
        if (cancelled) return;
        setPartners(Array.isArray(p) ? p : []);
        setOperators(Array.isArray(o) ? o : []);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const partnersById = new Map();
  partners.forEach((p) => {
    // `Id` FIRST: a partner's primary key is the Dynamoose hashKey `Id`
    // (PartnerSchema), which is what `counterparty.id` on a payment and
    // `counterparty_id` on a document both store. Reading only the lowercase
    // spellings indexed nothing at all, so every partner lookup missed and
    // the partner list came back empty.
    const id = p?.Id || p?.id || p?._id;
    if (id) partnersById.set(id, p);
  });
  const operatorsById = new Map();
  operators.forEach((o) => {
    const id = o?.Id || o?.id || o?._id;
    if (id) operatorsById.set(id, o);
  });

  return {
    partnersById, operatorsById, loading, error,
  };
}
